// Scale a recipe's ingredient amounts to a target serving count.
//
// Every ingredient row can carry its amount as:
//   - a structured `qty` { amount: number, unit: string }, OR
//   - a free-text `amount` string ("2 tbsp", "1½ cups", "a handful")
//
// We scale both. Structured qty is trivial — multiply `amount` by the
// scale factor. Free-text is best-effort — we peel off the leading
// numeric (integer, decimal, or unicode fraction, possibly mixed like
// "1½"), multiply it, and reattach the rest. Anything we can't parse
// stays as-is with a marker appended, so the user sees "(×2.0)" next
// to "a handful" instead of a silently-wrong number.
//
// Steps are not scaled — "cook for 12 minutes" stays 12 minutes even
// when you're making twice as much. Time / difficulty / pan-size
// heuristics could be added later; for v1 we only touch ingredients.

const UNICODE_FRACTIONS = {
  "¼": 0.25, "½": 0.5, "¾": 0.75,
  "⅓": 1 / 3, "⅔": 2 / 3,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
};

// Format a number for ingredient display — drop trailing .00, keep
// two decimals for fractions, convert common ratios back to unicode
// ("0.5" → "½") when it reads more naturally.
function formatAmount(n) {
  if (!Number.isFinite(n) || n <= 0) return "";
  // Try nice unicode first. Tolerance is deliberately loose-ish
  // (0.02) — scaled amounts land NEAR fractions constantly (¾ × ⅔ =
  // 0.5, ¼ × 0.72 = 0.18) and a cook reads "⅜" better than "0.37".
  const whole = Math.floor(n);
  const frac = n - whole;
  const fracEntries = Object.entries(UNICODE_FRACTIONS);
  for (const [sym, v] of fracEntries) {
    if (Math.abs(frac - v) < 0.02) {
      if (whole === 0) return sym;
      return `${whole}${sym}`;
    }
  }
  // Fall back to decimal, trimmed.
  const str = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return str.replace(/\.?0+$/, "");
}

// ── Kitchen measure descent ──────────────────────────────────────────
// Servings scaling produces amounts no measuring cup can hold: ¼ cup
// × 0.72 = "0.18 cups". Fraction-snapping alone can't save these —
// the nearest fraction (⅙) isn't a real scoop either. The kitchen-
// honest fix: snap to a STANDARD cup fraction when close enough,
// otherwise DESCEND a unit (0.18 cups → 2.88 tbsp → "3 tbsp") until
// the number lands on something a cook can physically measure.
// Applies only to US volume units; everything else keeps formatAmount.
const MEASURABLE_CUP_FRACS = [
  [0.25, "¼"], [1 / 3, "⅓"], [0.5, "½"], [2 / 3, "⅔"], [0.75, "¾"],
];
const UNIT_WORDS = {
  cup: "cup", cups: "cup",
  tbsp: "tbsp", tablespoon: "tbsp", tablespoons: "tbsp",
  tsp: "tsp", teaspoon: "tsp", teaspoons: "tsp",
};

function snapTo(value, targets, tolerance) {
  const whole = Math.floor(value);
  const frac = value - whole;
  let best = null;
  for (const [v, sym] of targets) {
    const diff = Math.abs(frac - v);
    if (!best || diff < best.diff) best = { v, sym, diff };
  }
  // Whole numbers count as snapped too (frac near 0 or 1).
  if (frac < tolerance) return { value: whole, text: whole > 0 ? String(whole) : null };
  if (1 - frac < tolerance) return { value: whole + 1, text: String(whole + 1) };
  if (best && best.diff <= tolerance) {
    return {
      value: whole + best.v,
      text: whole > 0 ? `${whole} ${best.sym}` : best.sym,
    };
  }
  return null;
}

// value + normalized unit → "3 tbsp" / "¼ cup" / "1 ½ cups" | null
// (null = not a volume unit or nothing measurable found; caller keeps
// the plain formatAmount path).
export function formatKitchenAmount(value, unitWord) {
  if (!Number.isFinite(value) || value <= 0) return null;
  const unit = UNIT_WORDS[String(unitWord || "").toLowerCase()];
  if (!unit) return null;
  // Tolerance: 5% of the amount, floored at 0.02 absolute — cooking
  // slack, deliberately looser than baking-scale precision because
  // scaled recipes are already approximations.
  const tol = (v) => Math.max(0.02, v * 0.05);

  if (unit === "cup") {
    const snapped = value >= 0.25 - tol(value)
      ? snapTo(value, MEASURABLE_CUP_FRACS, tol(value))
      : null;
    if (snapped?.text) {
      return `${snapped.text} ${snapped.value > 1 ? "cups" : "cup"}`;
    }
    // Descend: cups → tbsp (×16).
    return formatKitchenAmount(value * 16, "tbsp");
  }
  if (unit === "tbsp") {
    if (value >= 1 - tol(value)) {
      const snapped = snapTo(value, [[0.5, "½"]], tol(value));
      if (snapped?.text) return `${snapped.text} tbsp`;
    }
    // Descend: tbsp → tsp (×3).
    return formatKitchenAmount(value * 3, "tsp");
  }
  // tsp — the floor. Always produce something measurable: quarters
  // down to ⅛ tsp.
  const snapped = snapTo(value, [[0.125, "⅛"], [0.25, "¼"], [0.5, "½"], [0.75, "¾"]], tol(value));
  if (snapped?.text) return `${snapped.text} tsp`;
  const eighths = Math.max(1, Math.round(value * 8));
  const whole = Math.floor(eighths / 8);
  const rem = eighths % 8;
  const remSym = { 1: "⅛", 2: "¼", 3: "⅜", 4: "½", 5: "⅝", 6: "¾", 7: "⅞" }[rem];
  const text = whole > 0
    ? (remSym ? `${whole} ${remSym}` : String(whole))
    : remSym;
  return text ? `${text} tsp` : null;
}

// Extract a leading numeric RANGE ("2-3", "2–3", "1 to 2") from a
// free-text amount. Returns { lo, hi, sep, rest } or null. `sep` is
// reattached verbatim so "2-3 cloves" stays hyphenated and "1 to 2 tsp"
// keeps its words. Only integer/decimal endpoints — fraction-leading
// ranges fall through to the single-value path.
function parseLeadingRange(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.match(/^\s*(\d+(?:\.\d+)?)\s*(-|–|—|to)\s*(\d+(?:\.\d+)?)\s*(.*)$/i);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = Number(m[3]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo <= 0 || hi <= 0) return null;
  const sep = /to/i.test(m[2]) ? " to " : m[2];
  return { lo, hi, sep, rest: m[4] || "" };
}

// Extract a leading numeric from a free-text amount. Returns
// { value, rest } or null if nothing recognizable came off the front.
function parseLeadingNumeric(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.match(/^\s*(\d+(?:\.\d+)?)?\s*([¼½¾⅓⅔⅛⅜⅝⅞])?\s*(.*)$/);
  if (!m) return null;
  const intPart = m[1] ? Number(m[1]) : 0;
  const fracPart = m[2] ? UNICODE_FRACTIONS[m[2]] : 0;
  const value = intPart + fracPart;
  if (!Number.isFinite(value) || value <= 0) return null;
  return { value, rest: m[3] || "" };
}

// Scale a single ingredient row. Never mutates — returns a new object.
function scaleIngredient(ing, factor) {
  if (!ing || factor === 1) return ing;
  const out = { ...ing };
  // Structured qty path — trivial multiply.
  if (out.qty && typeof out.qty.amount === "number") {
    out.qty = { ...out.qty, amount: out.qty.amount * factor };
  }
  // Free-text amount path.
  if (typeof out.amount === "string" && out.amount.trim().length > 0) {
    // Range path first ("2-3 cloves" → "4-6 cloves"): scale BOTH
    // endpoints. Without this the single-value parse below peels only
    // the low end and leaves "4 -3 cloves".
    const range = parseLeadingRange(out.amount);
    if (range) {
      const lo = formatAmount(range.lo * factor);
      const hi = formatAmount(range.hi * factor);
      const rest = range.rest.trim();
      const scaledRange = `${lo}${range.sep}${hi}`;
      out.amount = rest ? `${scaledRange} ${rest}` : scaledRange;
      return out;
    }
    const parsed = parseLeadingNumeric(out.amount);
    if (parsed) {
      const scaled = parsed.value * factor;
      const rest = parsed.rest.trim();
      // Volume units get the kitchen-measure descent: "¼ cup" × 0.72
      // must come out "3 tbsp", never "0.18 cup". The descent replaces
      // BOTH the number and the unit word; whatever trails the unit
      // ("cup brown sugar" → "brown sugar") is reattached.
      const restWords = rest.split(/\s+/);
      const kitchen = rest
        ? formatKitchenAmount(scaled, restWords[0])
        : null;
      if (kitchen) {
        const trailing = restWords.slice(1).join(" ");
        out.amount = trailing ? `${kitchen} ${trailing}` : kitchen;
      } else {
        out.amount = rest
          ? `${formatAmount(scaled)} ${rest}`
          : formatAmount(scaled);
      }
    } else {
      // Can't parse a numeric — annotate honestly so the user knows
      // we didn't touch it. "a handful" + factor 2 → "a handful (×2)".
      const fFmt = formatAmount(factor);
      out.amount = `${out.amount} (×${fFmt})`;
    }
  }
  return out;
}

// Public: return a scaled copy of the recipe. Serves is updated,
// every ingredient's amount is scaled, everything else is preserved.
// No-op (returns the input unchanged) when scaling isn't meaningful.
export function scaleRecipe(recipe, targetServings) {
  if (!recipe) return recipe;
  const base = Number(recipe.serves) || 0;
  const target = Number(targetServings) || 0;
  if (!base || !target || base === target) return recipe;
  const factor = target / base;
  const ingredients = Array.isArray(recipe.ingredients)
    ? recipe.ingredients.map(ing => scaleIngredient(ing, factor))
    : recipe.ingredients;
  return { ...recipe, ingredients, serves: target };
}

// Pure scale factor — exposed for callers that only need a multiplier
// (e.g. nutrition rollups that have their own macros math).
export function scaleFactorFor(recipe, targetServings) {
  const base = Number(recipe?.serves) || 0;
  const target = Number(targetServings) || 0;
  if (!base || !target) return 1;
  return target / base;
}
